import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowRight, faBookHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { deleteTransactionDedication, updateTransaction } from 'api/transactions';
import {
    TransactionDedicationInput,
    TransactionsFormValues,
} from 'assets/js/pages/transactions/TransactionForm';
import * as DropdownMenu from 'components/dropdown-menu';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface TransactionDedicationForm
    extends Pick<
        TransactionsFormValues,
        | 'dedicationName'
        | 'dedicationType'
        | 'dedicationRecipientEmail'
        | 'dedicationRecipientName'
        | 'dedicationShouldNotifyRecipient'
    > {}

const defaultValues: Omit<TransactionDedicationForm, 'dedicationShouldNotifyRecipient'> = {
    dedicationName: '',
    dedicationType: 'in_honor_of',
    dedicationRecipientName: '',
    dedicationRecipientEmail: '',
};
const TransactionDedication = () => {
    const queryClient = useQueryClient();
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: transaction } = useCurrentTransactionOrById();
    const methods = Form.useForm<TransactionDedicationForm>({
        defaultValues: {
            dedicationName: transaction?.dedication?.name ?? defaultValues.dedicationName,
            dedicationType: transaction?.dedication?.type ?? defaultValues.dedicationType,
            dedicationRecipientName:
                transaction?.dedication?.recipient_name ?? defaultValues.dedicationRecipientName,
            dedicationRecipientEmail:
                transaction?.dedication?.recipient_email ?? defaultValues.dedicationRecipientEmail,
            dedicationShouldNotifyRecipient: !!transaction?.dedication?.recipient_email,
        },
    });
    const { handleSubmit, reset, resetFormButton } = methods;
    const { key, forceRender } = useForceRender();

    const onSuccess = (response?: Transaction) => {
        queryClient.setQueryData(
            transactionQueryKeys.transaction(accountId, transaction.id),
            (oldData: Transaction) => ({
                ...oldData,
                dedication: response?.dedication,
            }),
        );
        queryClient.invalidateQueries({
            queryKey: transactionQueryKeys.transactions(accountId),
        });
        forceRender();
    };

    const { isPending: isLoading, mutate: submit } = useMutation({
        mutationFn: (values: TransactionDedicationForm) => {
            const hasDedicationRecipientAndEmail =
                values.dedicationShouldNotifyRecipient &&
                values.dedicationRecipientEmail.length > 0 &&
                values.dedicationRecipientName.length > 0;

            return updateTransaction(transaction?.id!, {
                dedication: {
                    name: values.dedicationName,
                    type: values.dedicationType,
                    recipient_name: hasDedicationRecipientAndEmail
                        ? values.dedicationRecipientName
                        : null,
                    recipient_email: hasDedicationRecipientAndEmail
                        ? values.dedicationRecipientEmail
                        : null,
                },
            });
        },
        onSuccess: response => {
            success('Successfully updated transaction dedication!');
            onSuccess(response);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to update transaction dedication.');
            resetFormButton();
        },
    });

    const { mutate: deleteDedication } = useMutation({
        mutationFn: () => deleteTransactionDedication(transaction?.id!),
        onSuccess: () => {
            success('Deleted dedication successfully');
            onSuccess();
            methods.reset(defaultValues);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to delete transaction dedication.');
        },
    });

    return (
        <EditableInput.Root key={key}>
            <Box className={'flex flex-col gap-1 p-4'}>
                <EditableInput.ReadOnly className={'contents'}>
                    <div className={'flex w-full justify-between gap-4'}>
                        {transaction?.dedication ? (
                            <div className={'flex items-center gap-2'}>
                                <FontAwesomeIcon
                                    className={'text-brand-violet-primary'}
                                    icon={faBookHeart}
                                />
                                <p className={'font-medium'}>Dedication</p>
                            </div>
                        ) : (
                            <Collapsible.Trigger className={'flex items-center gap-2'}>
                                <FontAwesomeIcon
                                    className={'text-brand-violet-primary'}
                                    icon={faBookHeart}
                                />
                                <p>Add a dedication</p>
                                <FontAwesomeIcon icon={faArrowRight} />
                            </Collapsible.Trigger>
                        )}
                        {transaction?.dedication && (
                            <DropdownMenu.Root>
                                <DropdownMenu.EllipsisTrigger />
                                <DropdownMenu.Content align={'end'} sideOffset={5}>
                                    <Collapsible.Trigger asChild={true}>
                                        <DropdownMenu.Item>Edit</DropdownMenu.Item>
                                    </Collapsible.Trigger>
                                    <AlertDialog.Root type={'error'}>
                                        <AlertDialog.Trigger asChild={true}>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                Delete
                                            </DropdownMenu.Item>
                                        </AlertDialog.Trigger>
                                        <AlertDialog.Portal>
                                            <AlertDialog.Overlay />
                                            <AlertDialog.Content>
                                                <AlertDialog.Header>
                                                    <AlertDialog.Icon />
                                                    <AlertDialog.Close />
                                                </AlertDialog.Header>
                                                <AlertDialog.Body>
                                                    <AlertDialog.Title>
                                                        Delete dedication?
                                                    </AlertDialog.Title>
                                                    <AlertDialog.Description>
                                                        You won't be able to undo this action. Are
                                                        you sure?
                                                    </AlertDialog.Description>
                                                </AlertDialog.Body>
                                                <AlertDialog.Footer>
                                                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                    <AlertDialog.Action
                                                        onClick={() => deleteDedication()}
                                                    >
                                                        Delete
                                                    </AlertDialog.Action>
                                                </AlertDialog.Footer>
                                            </AlertDialog.Content>
                                        </AlertDialog.Portal>
                                    </AlertDialog.Root>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        )}
                    </div>
                    {transaction?.dedication && (
                        <>
                            <p>{transaction?.dedication?.sentence}</p>
                            {(transaction.dedication.recipient_email ||
                                transaction.dedication.recipient_name) && (
                                <div className={'flex flex-col'}>
                                    {transaction.dedication.recipient_name && (
                                        <p className={'text-sm'}>
                                            <span>Notification recipient</span>:{` `}
                                            {transaction?.dedication?.recipient_name}
                                            {` `}
                                            {transaction.dedication.recipient_email && (
                                                <span className={'text-gray-500'}>
                                                    ({transaction?.dedication?.recipient_email})
                                                </span>
                                            )}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </EditableInput.ReadOnly>
                <EditableInput.Content>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(
                                submit as SubmitHandler<TransactionDedicationForm>,
                            )}
                            className={'contents'}
                        >
                            <TransactionDedicationInput variant={'update'} />
                            <EditableInput.ActionsContainer>
                                <EditableInput.Cancel onClick={() => reset()} />
                                <Form.SubmitButton isSubmitting={isLoading} />
                            </EditableInput.ActionsContainer>
                        </form>
                    </Form.Provider>
                </EditableInput.Content>
            </Box>
        </EditableInput.Root>
    );
};

export { TransactionDedication };
